import { defineStore } from 'pinia';
import { useAuthStore } from './auth';
import { useGameStore } from './game';
import { createRoom, fetchRoomList, joinRoom, fetchRoomDetail, updateRoom } from '@/api';
import axios from 'axios';
import type { Room } from '../types';

let activeGameStore: ReturnType<typeof useGameStore> | null = null;

interface RoomState {
  currentRoom: Room | null;
  roomList: Room[];
  isLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  websocket: WebSocket | null;
  isGameStarted: boolean;
  isConnected: boolean; // Add connection state tracking
  playerLeftNotification: { username: string; timestamp: number } | null;
  pingInterval: ReturnType<typeof setInterval> | null;
  reconnectTimeout: ReturnType<typeof setTimeout> | null;
  retryCount: number;
  isIntentionalClose: boolean;

}

export const useRoomStore = defineStore('room', {
  state: (): RoomState => ({
    currentRoom: null,
    roomList: [],
    isLoading: false,
    error: null,
    websocket: null,
    isGameStarted: false,
    isConnected: false,
    playerLeftNotification: null,
    pingInterval: null,
    reconnectTimeout: null,
    retryCount: 0,
    isIntentionalClose: false,

  }),
  getters: {
    isInRoom: (state): boolean => !!state.currentRoom,

    isPlayerInRoom: (state): boolean => {
      const authStore = useAuthStore();
      return !!(
        state.currentRoom &&
        state.currentRoom.room_players &&
        authStore.user &&
        state.currentRoom.room_players.some((roomPlayer) => roomPlayer && roomPlayer.player.id === authStore.user?.id)
      );
    },

    isHost: (state): boolean => {
      const authStore = useAuthStore();
      return !!(state.currentRoom && authStore.user && state.currentRoom.host.id === authStore.user.id);
    },
  },
  actions: {
    async createRoom(roomName: string, maxPlayers: number, hostName: string, isPrivate: boolean, roomType: string = 'zoom_out') {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await createRoom(roomName, maxPlayers, hostName, isPrivate, roomType);
        this.currentRoom = response;
        return response;
      } catch (error) {
        this.error = error;
        console.error('Room creation error:', error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async updateRoomMode(roomId: string, roomType: string) {
      this.isLoading = true;
      try {
        const response = await updateRoom(roomId, { room_type: roomType });
        this.currentRoom = response; // Update local state
        return response;
      } catch (error) {
        console.error('Update room mode error:', error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchRoomList() {
      this.isLoading = true;
      this.error = null;
      try {
        console.log('Room Store: Calling fetchRoomList. Current Authorization header:', axios.defaults.headers.common['Authorization']);
        const response = await fetchRoomList();
        if (Array.isArray(response)) {
          this.roomList = response;
        } else if (response.rooms) {
          this.roomList = response.rooms;
        } else {
          this.roomList = [];
        }
      } catch (error) {
        this.error = error;
        console.error('Fetch room list error:', error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async joinRoom(roomId: string, playerName?: string) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await joinRoom(roomId, playerName);
        return response;
      } catch (error) {
        this.error = error;
        console.error('Join room error:', error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async joinRoomByInviteCode(inviteCode: string) {
      this.isLoading = true;
      this.error = null;
      try {
        // NOTE: joinRoomByInviteCode needs to be imported from api
        // But currently I only imported: createRoom, fetchRoomList, joinRoom, fetchRoomDetail
        // I need to update imports or just use axios directly if lazy (but better to use api module)
        // I'll assume I update imports below via another replace or just rely on dynamic import or updated import
        // Wait, I can't easily update imports in this chunk if it's far away.
        // I will use `import { ... } from '@/api'` and add `joinRoomByInviteCode` to it.
        // Since api/index.ts is already updated, I just need to update usage here.
        // I need to update the import statement at the top of the file as well.
        // I'll do that in a separate chunk or if I can here.
        // Actually, I can't see the import lines here (line 4).
        // I'll assume I'll fix imports separately.
        const response = await import('@/api').then(m => m.joinRoomByInviteCode(inviteCode));
        // Don't set currentRoom here because this is just a check/lookup.
        // this.currentRoom = response; // Join usually returns room detail
        return response;
      } catch (error) {
        // Do not set global error for invite code failure, as it is handled in UI
        console.error('Join room by invite code error:', error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchRoomDetail(roomId: string) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await fetchRoomDetail(roomId);
        this.currentRoom = response;
        // Sync isGameStarted with room status
        this.isGameStarted = this.currentRoom?.status === 'playing';
        return response;
      } catch (error) {
        this.error = error;
        console.error(`Fetch room detail error (Room ID: ${roomId}):`, error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    setCurrentRoom(room: Room | null) {
      this.currentRoom = room;
    },
    clearCurrentRoom() {
      this.currentRoom = null;
      this.isGameStarted = false;
    },

    registerGameStore(store: ReturnType<typeof useGameStore>) {
      activeGameStore = store;
      console.log('roomStore: gameStore registered successfully. activeGameStore is now:', !!activeGameStore);
    },

    async connectGlobalWebSocket(gameStore?: ReturnType<typeof useGameStore>, endpoint: string = 'ws/game/global/') {
      this.isIntentionalClose = false;
      console.log(`--- Attempting to connect WebSocket (${endpoint}) ---`);

      if (gameStore) {
        this.registerGameStore(gameStore);
      } else if (!activeGameStore) {
        activeGameStore = useGameStore();
      }

      const API_WS_BASE_URL = import.meta.env.VITE_API_WS_BASE_URL;
      if (!API_WS_BASE_URL) {
        console.error('VITE_API_WS_BASE_URL is not defined in .env');
        return;
      }
      const authStore = useAuthStore();
      const token = authStore.token;

      // Construct Target URL
      // Ensure endpoint doesn't start with / if base has it, or handle cleanly.
      // Assuming API_WS_BASE_URL doesn't end with slash, and endpoint doesn't start with one?
      // Let's be safe.
      const baseUrl = API_WS_BASE_URL.replace(/\/$/, '');
      const path = endpoint.replace(/^\//, '');
      let wsUrl = `${baseUrl}/${path}`;

      // Check current connection
      if (this.websocket) {
        const currentUrl = this.websocket.url.split('?')[0]; // Ignore query params for comparison
        // Note: websocket.url is absolute. wsUrl is absolute.
        // We might need to ensure wsUrl has protocol if not provided? 
        // VITE_API_WS_BASE_URL usually includes protocol.

        // If switching endpoints (e.g. global -> single_player), force close
        if (currentUrl !== wsUrl && this.websocket.readyState !== WebSocket.CLOSED) {
          console.log(`Switching WebSocket endpoint from ${currentUrl} to ${wsUrl}. Closing existing connection.`);
          this.isIntentionalClose = true; // Mark as intentional to prevent auto-reconnect temporarily
          this.websocket.close(1000, 'Switching endpoint');
          this.websocket = null;
          this.isIntentionalClose = false; // Reset for new connection
        } else if (this.websocket.readyState === WebSocket.OPEN) {
          console.log('WebSocket is already connected to correct endpoint. Skipping reconnection.');
          this.isConnected = true;
          if (activeGameStore) {
            activeGameStore.setWebSocket(this.websocket);
          }
          return;
        } else if (this.websocket.readyState === WebSocket.CONNECTING) {
          console.log('WebSocket is connecting, waiting...');
          // ... (existing promise logic) ...
          return new Promise<void>((resolve) => {
            const ws = this.websocket;
            if (!ws) return resolve();
            const onOpen = () => {
              ws.removeEventListener('open', onOpen);
              console.log('WebSocket connection established (from waiting).');
              this.isConnected = true;
              if (activeGameStore) {
                activeGameStore.setWebSocket(ws);
              }
              resolve();
            };
            ws.addEventListener('open', onOpen);
          });
        }
      }

      if (token) {
        // Append token. Check if query params already exist in endpoint (unlikely but safe)
        wsUrl += wsUrl.includes('?') ? `&token=${token}` : `?token=${token}`;
      }

      this.websocket = new WebSocket(wsUrl);
      this.websocket.binaryType = 'arraybuffer';

      if (activeGameStore) {
        activeGameStore.setWebSocket(this.websocket);
      }

      return new Promise<void>((resolve, reject) => {
        if (!this.websocket) return;

        this.websocket.onopen = () => {
          console.log('WebSocket connection established:', wsUrl);
          this.isConnected = true;
          this.retryCount = 0; // Reset retry count
          if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
          }
          this.startPingTimer();
          resolve();
        };

        this.websocket.onmessage = async (event) => {
          await this.handleWebSocketMessage(event);
        };

        this.websocket.onclose = (event) => {
          console.log('WebSocket connection closed:', event.code, event.reason);
          this.isConnected = false;

          // Notify GameStore
          if (activeGameStore) {
            activeGameStore.setWebSocket(null);
          }

          if (this.websocket === event.target) {
            this.websocket = null;
          }

          // Auto-reconnect execution
          if (!this.isIntentionalClose) {
            const timeout = Math.min(1000 * (2 ** this.retryCount), 30000); // Exponential backoff max 30s
            console.log(`Connection lost. Reconnecting in ${timeout}ms... (Retry: ${this.retryCount})`);
            this.retryCount++;

            this.reconnectTimeout = setTimeout(() => {
              this.connectGlobalWebSocket(gameStore, endpoint); // Keep original endpoint
            }, timeout);
          }
        };

        this.websocket.onerror = (error) => {
          console.error('WebSocket error:', error);
          reject(error);
        };
      });
    },

    // Deprecated/Legacy support wrapper, or logic to redirect
    async connectRoomWebSocket(roomId: string, gameStore?: ReturnType<typeof useGameStore>) {
      if (roomId === 'single_player_room') {
        // Connect to specific single player endpoint
        // Note: URL structure in routing.py is ws/rooms/single_player_room/
        await this.connectGlobalWebSocket(gameStore, `ws/rooms/${roomId}/`);
        return;
      }

      console.warn('connectRoomWebSocket is deprecated. Using connectGlobalWebSocket + joinRoomChannel.');
      await this.connectGlobalWebSocket(gameStore);
      this.joinRoomChannel(roomId);
    },

    joinRoomChannel(roomId: string) {
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        this.websocket.send(JSON.stringify({
          type: 'join_room',
          room_id: roomId
        }));
        console.log(`Sent to join_room: ${roomId}`);
      } else {
        console.warn('Cannot join room immediately: WebSocket not connected. Attempting to connect...');
        // Fallback: try to connect then join?
        this.connectGlobalWebSocket().then(() => {
          if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            this.websocket.send(JSON.stringify({
              type: 'join_room',
              room_id: roomId
            }));
          }
        });
      }
    },

    leaveRoomChannel() {
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        this.websocket.send(JSON.stringify({
          type: 'leave_room'
        }));
        console.log('Sent leave_room request.');
        this.clearCurrentRoom(); // Properly reset state including isGameStarted
      }
    },

    async handleWebSocketMessage(event: MessageEvent) {
      // 毎回最新のストアを取得する（変数のキャッシュを使わない）
      const gameStore = useGameStore();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let data: any;
      let imageUrl: string | null = null;
      let arrayBuffer: ArrayBuffer | undefined;

      try {
        if (event.data instanceof ArrayBuffer) {
          arrayBuffer = event.data;
        } else if (event.data instanceof Blob) {
          arrayBuffer = await event.data.arrayBuffer();
        } else if (typeof event.data === 'string') {
          data = JSON.parse(event.data);
        } else {
          console.error('[ERROR roomStore] Unexpected data type:', event.data);
          return;
        }

        if (arrayBuffer) {
          const dataView = new DataView(arrayBuffer);
          const headerLength = dataView.getUint32(0, false);
          const metadataBytes = new Uint8Array(arrayBuffer, 4, headerLength);
          const metadataString = new TextDecoder("utf-8").decode(metadataBytes);
          data = JSON.parse(metadataString);

          const imageBytes = new Uint8Array(arrayBuffer, 4 + headerLength);
          const blob = new Blob([imageBytes], { type: 'image/webp' });
          imageUrl = URL.createObjectURL(blob);
        }
      } catch (e) {
        console.error('[ERROR roomStore] Failed to process WebSocket message:', e);
        return;
      }

      const logData = { ...data };
      if (imageUrl) {
        logData.image_url = '[Blob URL]';
      }



      switch (data.type) {
        case 'room_update':
          this.setCurrentRoom(data.room);
          break;
        case 'room_list_update':
          console.log('Received room list update via WebSocket');
          if (Array.isArray(data.room_list)) {
            this.roomList = data.room_list;
          } else if (data.room_list && data.room_list.rooms) {
            this.roomList = data.room_list.rooms;
          } else {
            console.warn('Unknown room_list format:', data.room_list);
          }
          break;
        case 'player_joined':
        case 'player_left':
          this.setCurrentRoom(data.room);
          if (data.type === 'player_left') {
            this.playerLeftNotification = {
              username: data.username,
              timestamp: Date.now(),
            };
            setTimeout(() => {
              if (this.playerLeftNotification && this.playerLeftNotification.timestamp === data.timestamp) {
                this.playerLeftNotification = null;
              }
            }, 3000);
          }
          break;
        case 'left_room_success':
          console.log('Successfully left room');
          this.currentRoom = null;
          break;
        case 'player_inactive':
          console.log(`Player ${data.username} is inactive.`);
          break;
        case 'game_started':
          console.log('Game started notification received!');
          this.isGameStarted = true;
          if (gameStore && typeof gameStore.gameStarted === 'function') {
            gameStore.gameStarted(data);
          }
          break;
        case 'send_question_data':
          if (gameStore) {
            if (gameStore.answerResult) {
              setTimeout(() => {
                gameStore!.setQuestionData(data, imageUrl);
              }, 500);
            } else {
              gameStore.setQuestionData(data, imageUrl);
            }
          }
          break;
        case 'send_image_frame':
          if (gameStore) gameStore.setImageFrame(data, imageUrl);
          break;
        case 'all_images_sent':
          if (gameStore) gameStore.allImagesSent(data);
          break;
        case 'game_finished':
          if (gameStore) gameStore.gameFinished(data);
          break;
        case 'player_answered_update':
          if (gameStore && typeof gameStore.updatePlayerAnsweredStatus === 'function') {
            gameStore.updatePlayerAnsweredStatus(data);
          }
          break;
        case 'answer_result':
          if (gameStore) gameStore.handleAnswerResult(data, imageUrl);
          break;
        case 'answer_result_group':
          if (gameStore) gameStore.handleAnswerResultGroup(data, imageUrl);
          break;
        case 'answer_stats':
          if (gameStore && typeof gameStore.handleAnswerStats === 'function') {
            gameStore.handleAnswerStats(data);
          } else {
            console.error('[RoomStore] handleAnswerStats not found in gameStore!');
          }
          break;
        case 'leaderboard_update':
          if (gameStore && typeof gameStore.handleLeaderboardUpdate === 'function') {
            gameStore.handleLeaderboardUpdate(data);
          } else {
            console.error('[RoomStore] handleLeaderboardUpdate not found in gameStore!');
          }
          break;
        case 'error':
          console.error('WebSocket Error Message:', data.message);
          break;
        default:
          console.warn('Unknown WebSocket message type:', data.type);
          break;
      }
    },

    disconnectRoomWebSocket() {
      this.stopPingTimer();
      this.isIntentionalClose = true;

      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
      this.retryCount = 0;

      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        this.websocket.close(1000, 'User navigated away from room');
        console.log('WebSocket connection closed (roomStore).');
      }
      this.websocket = null;
      if (activeGameStore) {
        activeGameStore.setWebSocket(null);
      }
      this.isGameStarted = false;
      // Reset isIntentionalClose done? No, keep it true so lingering close events don't trigger reconnect
      // But verify if we reuse store... store instance persists.
      // So if user navigates back, connectGlobalWebSocket will set isIntentionalClose = false.
    },

    sendGameStart(roomId: string) {
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        this.websocket.send(
          JSON.stringify({
            type: 'start_game',
            room_id: roomId,
          })
        );
        console.log('Game start request sent (roomStore).');
      } else {
        console.error('WebSocket is not connected (roomStore). Cannot send game start request.');
        alert('Cannot start game. Please check WebSocket connection.');
      }
    },

    sendAnswer(questionId: number, choiceId: number, timeTaken: number) {
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        this.websocket.send(
          JSON.stringify({
            type: 'answer_submitted',
            question_id: questionId,
            choice_id: choiceId,
            time_taken: timeTaken,
          })
        );
        console.log('Answer sent (roomStore).');
      } else {
        console.error('WebSocket is not connected (roomStore). Cannot send answer.');
      }
    },

    sendNextImageFrameRequest(questionId: number, currentImageIndex: number) {
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        this.websocket.send(
          JSON.stringify({
            type: 'next_image_frame',
            question_id: questionId,
            current_image_index: currentImageIndex,
          })
        );
      } else {
        console.error('WebSocket is not connected (roomStore). Cannot send next image frame request.');
      }
    },

    startPingTimer() {
      this.stopPingTimer();

      this.pingInterval = setInterval(() => {
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
          this.websocket.send(JSON.stringify({ type: 'ping' }));

        }
      }, 10000);
    },

    stopPingTimer() {
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }
    },
  },
});
